import React, { Children, isValidElement, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import ContentDate from '../common/ContentDate';

const Modal = ({
  isOpen,
  onClose,
  children,
  showContentDate = false,
}) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const backdropVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    }),
    [],
  );

  const modalVariants = useMemo(
    () => ({
      hidden: {
        opacity: 0,
        scale: 0.95,
        y: 20,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
      },
    }),
    [],
  );

  const transition = useMemo(
    () => ({
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1],
    }),
    [],
  );

  const childManagesContentDate = Children.toArray(children).some((child) => {
    return isValidElement(child) && child.type?.managesContentDate === true;
  });

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <AnimatePresence mode="wait">
      <motion.div
        className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
        initial="hidden"
        animate="visible"
        exit="hidden"
        variants={backdropVariants}
        transition={transition}
      >
        <motion.div
          className="absolute inset-0 bg-black/60"
          onClick={onClose}
          variants={backdropVariants}
          transition={transition}
        />
        <motion.div
          className="relative bg-white rounded-xl shadow-2xl w-full modal-container"
          style={{
            maxWidth: '900px',
            maxHeight: '85vh',
            overflow: 'auto',
            padding: '24px',
            willChange: 'transform,opacity',
            transform: 'translateZ(0)',
          }}
          onClick={(event) => event.stopPropagation()}
          variants={modalVariants}
          transition={transition}
          role="dialog"
          aria-modal="true"
        >
          {showContentDate && !childManagesContentDate && <ContentDate />}
          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

export default Modal;